// 剣舞の新しい技「跳んで叩く」(2026-10-25・開発版 ?bladeDelta=1)。アルレッキーノ (Lies of P) の、宙で一度止まってから真上から落ちる技を落とし込んだ。
//   作者「剣舞の技の数は全然増やしていいよ。ひとまずアルレッキーノを剣舞に落とし込む形で作ってもらって、実プレイしながら調整していきたい。」
//   技の組み立て = docs/research/2026-10-25-arlecchino-attack-analysis.md (跳んで叩く δ)。主人公は跳べないので、落ちてくる所を下で弾く形。
//   流れ: 二刀を背へ回して深く沈む → 跳び上がって宙で丸まって回る → 頂点で剣を下へ揃えて一度止まる
//         → 真上から落ちて剣を床へ突き立てる (1発目) → 一拍おいて、低く踏み込んで足もとを払う (2発目・引き寄せの払いと同じ絵) → 立ち姿。
//   2発とも弾ける。宙にいる間は絵だけを上げる (キーの offset)。横の移動は溜めの後ろの詰め (preActiveLunge) で、主人公の真上まで来て落ちる。
//   絵 = public/sprites/bosses/blade-dancer/m1025_d_{1..4}.png (横の基準は sprites.ts の剣舞の表)。画面 px は絵の画素 × 0.2718。
//   突き立てた剣先 (画面 px・足元から前): d4 の2本 (83, 0)・(105, 0)。
import { ATTACKS } from './attacks'
import { PLAYER_DAMAGE } from './balance'
import { BOSS_DEFINITIONS } from './bosses'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { SMEAR_ARTS, preloadBladePetals, type ContactSmearArc } from './render'
import type { AttackDef } from './types'
import { BLADE_SMEAR_BASE, bladeDancerV6Smear, TENSE, withDissolves, type BladeTrailStyle } from './blade_dancer_art'
import { BLADE_PULL_SMEAR, BLADE_PULL_STRIKES } from './blade_dancer_pull'
import { BLADE_PULL_ID } from './blade_dancer_pull_ribbon'

const BOSS_ID = 'blade-dancer'
export const BLADE_DELTA_ID = 'bladeDescent'
const D = (n: number) => `m1025_d_${n}`
const SWEEP = 'm1025_c_5'            // 低く踏み込んで足もとを払う (引き寄せの2発目と同じ絵)
const SETTLE = 'm1020_waltz_s2'
const STAND = 'm1020_waltz_c6'

const TICK = 16
const ACTIVE_MS = 130
const SWEEP_AFTER_MS = 560           // 突き立ててから払いまで (一拍おく)
export const BLADE_DELTA_GAPS_MS = [SWEEP_AFTER_MS - ACTIVE_MS - TICK]
const CHARGE_MS = 1700
const RECOVERY_MS = 950

// 宙の高さ (上が負)。跳び出し → 頂点で止まる → 落ちる。−250 では頂点の体が画面の上へ切れた (撮影 10-25)。
export const BLADE_DELTA_PEAK_PX = -130
const LAUNCH_AT = 0.36               // 床を離れる
const PEAK_AT = 0.56                 // 頂点に着いて止まる
const DROP_AT = 0.86                 // 落ち始める (落ちるのは溜めの残り 0.14 = 約 0.24 秒)
export const BLADE_DELTA_TIMING = { LAUNCH_AT, PEAK_AT, DROP_AT } as const

const TIP_FWD_PX = 105
const REACH_FIT_MARGIN_PX = 15
const REAR_PX = -60
export const BLADE_DELTA_STRIKES = [
  { localFromPx: REAR_PX, localToPx: TIP_FWD_PX + REACH_FIT_MARGIN_PX },   // 落ちてくる体と、突き立てる剣
  { ...BLADE_PULL_STRIKES[1] },                                          // 低い払い
] as const
// 跳んでいる間 (床を離れてから落ちきるまで) に主人公の真上まで寄る。剣先が主人公に届く所で止まる。
const LEAP_MS = Math.round(CHARGE_MS * (1 - LAUNCH_AT))
const LEAP_PX = 340
const LAND_GAP_PX = TIP_FWD_PX + 5        // 剣先が主人公の前の端に刺さる所 (−15 では体が主人公に深く重なった・撮影 10-25)
const APPROACH_PX = 420

export const BLADE_DELTA_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SETTLE, hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.06, image: D(1), hold: true, scale: { x: 1.04, y: 0.93 }, ease: 'easeIn' },        // 二刀を背へ回して深く沈む
  { phase: 'telegraph', at: 0.3, image: D(1), hold: true, scale: { x: 1.06, y: 0.9 }, ...TENSE, ease: 'easeOut' },
  { phase: 'telegraph', at: LAUNCH_AT, image: D(2), hold: true, offset: { y: -40 }, ease: 'easeOut' },            // 跳び上がって宙で丸まって回る
  { phase: 'telegraph', at: PEAK_AT, image: D(3), hold: true, offset: { y: BLADE_DELTA_PEAK_PX } },              // 頂点で剣を下へ揃えて止まる
  { phase: 'telegraph', at: DROP_AT, image: D(3), hold: true, offset: { y: BLADE_DELTA_PEAK_PX - 8 }, ease: 'easeIn' },
  { phase: 'active', at: 0, image: D(4), hold: true, tag: 'contact', hitIndex: 0, offset: { y: 0 }, scale: { x: 1.07, y: 0.92 } }, // 1発目: 真上から落ちて剣を床へ突き立てる (着地で潰れる)
  { phase: 'comboGap', at: 0, image: D(4), hold: true, tag: 'followThrough', hitIndex: 0, scale: { x: 1.03, y: 0.96 } },
  { phase: 'comboGap', at: 0.4, image: D(4), hold: true, hitIndex: 0, ease: 'easeOut' },                       // 潰れから戻る
  { phase: 'comboGap', at: 0.78, image: D(4), hold: true, hitIndex: 0, ...TENSE, ease: 'easeIn' },
  { phase: 'active', at: 0, image: SWEEP, hold: true, tag: 'contact', hitIndex: 1, offset: { x: 6 }, scale: { x: 1.05, y: 0.95 }, rot: 2 }, // 2発目: 剣を抜きざまに低く踏み込んで払う
  // 10-28 締めは五連と同じく段を踏んで立ち上がる。
  { phase: 'recovery', at: 0, image: SWEEP, hold: true, tag: 'followThrough', offset: { x: 10 }, rot: 3, ease: 'easeOut' },
  { phase: 'recovery', at: 0.3, image: 'm1020_v6_g2', hold: true, tag: 'recover', ease: 'easeOut', soft: true },   // 10-28 低い踏み込みから半ば立ち上がる絵を挟む (剣を掲げる s1 へ一足に跳ねない)
  { phase: 'recovery', at: 0.5, image: SETTLE, hold: true, ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.7, image: STAND, hold: true, ease: 'easeOut' },
  { phase: 'recovery', at: 0.9, image: 'idle', hold: true },
], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })

// 1発目は上から剣先の間 (94, 0) へ落ちるわずかに曲がった筋。床から花びらと火花が上へ舞う。2発目は引き寄せの払いと同じ弧。
export const BLADE_DELTA_SMEAR: Record<string, ContactSmearArc> = {
  [D(4)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: -106, anchorUpPx: 0, radiusPx: 200, angleFromRad: 0.35, angleToRad: 0, widthPx: 8,
    sparks: true, petals: { spread: 'up', count: 16, seed: 31 } },
  [SWEEP]: BLADE_PULL_SMEAR[SWEEP]!,
}

const DEF: AttackDef = {
  id: BLADE_DELTA_ID,
  label: '跳んで叩く',
  kind: 'physical',
  chargeMs: CHARGE_MS, activeMs: ACTIVE_MS, recoveryMs: RECOVERY_MS,
  parryable: true, defense: 'parry',
  damage: PLAYER_DAMAGE.standard + 6, postureDamage: 32,
  comboHits: 2, comboGapMs: BLADE_DELTA_GAPS_MS[0], comboGapsMs: [...BLADE_DELTA_GAPS_MS],
  range: BLADE_DELTA_STRIKES[0].localToPx, approachDistancePx: APPROACH_PX,
  strike: { ...BLADE_DELTA_STRIKES[0] },
  comboStages: [
    { strike: { ...BLADE_DELTA_STRIKES[0] } },
    { strike: { ...BLADE_DELTA_STRIKES[1] }, postureDamage: 22 },
  ],
  // 跳んでいる間に寄る。溜めの 3 割より長いが、ここは「宙を渡る」動きそのもの (止まって見せる沈みは前の 0.36)。
  preActiveLungeMs: LEAP_MS, preActiveLungeDistance: LEAP_PX,
  preActiveLungeStopGapPx: LAND_GAP_PX, preActiveLungeEase: 'smooth',
  // 着地した所から動かずに払う。
  comboLunge: { followHits: 0, followSpeed: 0, advanceSpeed: 0, plantFromHit: 0, gapOnly: true },
  sourceBossId: BOSS_ID,
}

let installed = false

/** 剣舞に「跳んで叩く」を足す (DEV ?bladeDelta=1)。最初から出る技の組に入れる。 */
export function useBladeDancerDelta(trail: BladeTrailStyle = 'afterimage'): void {
  if (!installed) {
    ATTACKS[BLADE_DELTA_ID] = { ...DEF }
    const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
    def.baseAttackIds.push(BLADE_DELTA_ID)
    def.unlockByExecutes?.[0]?.ids.push(BLADE_DELTA_ID)
    installed = true
  }
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), [BLADE_DELTA_ID]: BLADE_DELTA_TIMELINE }
  const smear = bladeDancerV6Smear(trail, BLADE_DELTA_SMEAR)
  // 突き立てる1発目は帯にしない (花びらと火花のまま)。
  smear[D(4)] = BLADE_DELTA_SMEAR[D(4)]!
  Object.assign(SMEAR_ARTS, smear)
  preloadBladePetals()
}

/** 外す (テストの後始末用)。払いの跡は引き寄せと同じ絵なので、引き寄せが入っている時は残す。 */
export function restoreBladeDancerDelta(): void {
  if (!installed) return
  delete ATTACKS[BLADE_DELTA_ID]
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  def.baseAttackIds = def.baseAttackIds.filter(id => id !== BLADE_DELTA_ID)
  const first = def.unlockByExecutes?.[0]
  if (first) first.ids = first.ids.filter(id => id !== BLADE_DELTA_ID)
  delete BOSS_ATTACK_TIMELINES[BOSS_ID]?.[BLADE_DELTA_ID]
  delete SMEAR_ARTS[D(4)]
  if (!ATTACKS[BLADE_PULL_ID]) delete SMEAR_ARTS[SWEEP]
  installed = false
}

export function parseBladeDelta(v: string | null): boolean {
  return v === '1'
}
